import { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import useOrgStore from '../../store/orgStore';

const TOOLBAR_ITEMS = [
  { key: 'pessoa',  label: 'Pessoa',     description: 'Filtrar cards por responsável ou criador.' },
  { key: 'filtro',  label: 'Filtro',     description: 'Filtrar por status e outras colunas.' },
  { key: 'ordenar', label: 'Ordenar',    description: 'Ordenar cards por colunas.' },
  { key: 'agrupar', label: 'Agrupar por', description: 'Agrupar cards por coluna de status ou responsável.' },
];

const DEFAULT_VISIBILITY = { pessoa: true, filtro: true, ordenar: true, agrupar: true };

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-accent' : 'bg-white/10'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export default function CustomizePage() {
  const { org, fetchOrg, updateOrgConfig } = useOrgStore();
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!org) fetchOrg().catch(() => {});
  }, [org, fetchOrg]);

  const visibility = { ...DEFAULT_VISIBILITY, ...(org?.config?.toolbarVisibility ?? {}) };

  const handleToggle = async (key, value) => {
    setSaving(key);
    setError(null);
    try {
      await updateOrgConfig({ toolbarVisibility: { ...visibility, [key]: value } });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  if (!org) {
    return (
      <div className="p-8">
        <div className="h-6 w-48 bg-white/5 rounded animate-pulse mb-4" />
        <div className="h-4 w-80 bg-white/5 rounded animate-pulse mb-8" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-14 bg-white/5 rounded-lg animate-pulse mb-3" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-8">
      <div className="flex items-center gap-3 mb-2">
        <SlidersHorizontal className="w-5 h-5 text-accent" />
        <h1 className="text-xl font-semibold text-text-primary">Personalizar</h1>
      </div>

      <div className="mb-8">
        <h2 className="text-base font-semibold text-text-primary mb-1">Toolbar dos quadros</h2>
        <p className="text-sm text-text-secondary">
          Controle quais filtros aparecem para os usuários nos quadros. Mudanças se aplicam a todos os quadros da organização.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2 rounded-lg bg-danger/10 border border-danger/30 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {TOOLBAR_ITEMS.map(({ key, label, description }) => (
          <div
            key={key}
            className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg bg-card border border-border"
          >
            <div>
              <div className="text-sm font-medium text-text-primary">{label}</div>
              <div className="text-xs text-text-secondary mt-0.5">{description}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {saving === key && (
                <span className="text-xs text-text-secondary">Salvando...</span>
              )}
              <Toggle
                checked={visibility[key]}
                onChange={(val) => handleToggle(key, val)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
